import { ALL_MODULES, isPrivilegedRole, type ModuleKey } from "@adpilot/shared";
import { redirect } from "@tanstack/react-router";
import { useAuthStore } from "@/stores/auth";

/**
 * 模块权限门控(ADP-167)。路由 beforeLoad 用:子账号访问未授权模块时
 * 重定向到其首个有权模块;后端同样按模块派生权限拒绝写操作,前端门控
 * 只是体验层。
 */

const MODULE_HOME_ROUTES: Record<ModuleKey, string> = {
  dashboard: "/",
  campaigns: "/campaigns",
  ai: "/managed-groups",
  approvals: "/ai-operations",
  settings: "/settings",
};

export function grantedModulesOf(
  user: {
    isMainAccount?: boolean;
    role?: string | null;
    modulePermissions?: string[] | null;
  } | null,
): readonly ModuleKey[] {
  if (!user) return ALL_MODULES;
  // ADP-180: admin 与 owner 同为全模块,不依赖其 modulePermissions 恰好为 null。
  if (user.isMainAccount || isPrivilegedRole(user.role)) return ALL_MODULES;
  return (user.modulePermissions ?? ALL_MODULES) as ModuleKey[];
}

/** The landing route for a user — first granted module in nav order. */
export function firstGrantedRoute(
  user: {
    isMainAccount?: boolean;
    role?: string | null;
    modulePermissions?: string[] | null;
  } | null,
): string {
  const granted = grantedModulesOf(user);
  const first = ALL_MODULES.find((key) => granted.includes(key));
  return first ? MODULE_HOME_ROUTES[first] : "/";
}

/**
 * beforeLoad guard: throw a redirect when the current user lacks `module`.
 * Must run AFTER the parent `_authed` beforeLoad hydrated the auth store.
 */
export function requireModule(module: ModuleKey): void {
  requireAnyModule([module]);
}

/** Shared read/stop pages can admit either module without granting write permissions. */
export function requireAnyModule(modules: readonly ModuleKey[]): void {
  const user = useAuthStore.getState().user;
  if (!user) return; // parent guard handles unauthenticated
  const granted = grantedModulesOf(user);
  // Empty grants can only mean corrupted data (schema enforces min 1): let
  // the page render instead of redirect-looping — the server still rejects
  // unauthorized writes.
  if (granted.length === 0) return;
  if (!modules.some((module) => granted.includes(module))) {
    throw redirect({ to: firstGrantedRoute(user) });
  }
}
